import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SchoolAnalytics, StudentListResponse, StudentResult } from '@/types/admin';

export function useSchoolAnalytics(schoolId: string | null) {
  const [data, setData] = useState<SchoolAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.rpc('get_school_analytics' as never, { p_school_id: schoolId });
    if (error) setError(error.message);
    else setData(data as unknown as SchoolAnalytics);
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
}

export function useSchoolStudents(schoolId: string | null, search: string = '', page: number = 0) {
  const [data, setData] = useState<StudentListResponse>({ students: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    supabase.rpc('get_school_students' as never, {
      p_school_id: schoolId,
      p_search: search || null,
      p_limit: 25,
      p_offset: page * 25
    }).then(({ data, error }: { data: unknown; error: unknown }) => {
      if (!error && data) setData(data as unknown as StudentListResponse);
      setLoading(false);
    });
  }, [schoolId, search, page]);

  return { ...data, loading };
}

export function useStudentResults(studentUserId: string | null, schoolId: string | null) {
  const [data, setData] = useState<StudentResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!studentUserId || !schoolId) return;
    setLoading(true);
    supabase.rpc('get_student_results' as never, { p_student_user_id: studentUserId, p_school_id: schoolId })
      .then(({ data, error }: { data: unknown; error: unknown }) => {
        if (!error && data) setData(data as unknown as StudentResult);
        setLoading(false);
      });
  }, [studentUserId, schoolId]);

  return { data, loading };
}